import React, { useEffect, useMemo, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { ModalOverlay } from "../../../components/ModalOverlay";
import CertificateCanvas from "../../setting/manageprint/certificatePrint/CertificateCanvas";
import {
  findTemplate,
  hydrateCertificateStore,
  loadCertificateStore,
  printCertificateSheet,
  saveCertificateStore,
} from "../../setting/manageprint/certificatePrint/certificateStorage";
import { CertificateDesignerStore } from "../../setting/manageprint/certificatePrint/certificateTypes";
import {
  buildCertificatePrintData,
  formatCertificateDate,
  inclusiveLeaveDays,
} from "../../setting/manageprint/certificatePrint/buildCertificatePrintData";
import { useLoggedInClinic } from "../../setting/manageprint/certificatePrint/useLoggedInClinic";
import { resolveCertificateLogo } from "../../setting/manageprint/certificatePrint/getLoggedInClinic";
import baseUrl from "../../../api/baseUrl";
import type { SummaryTheme } from "./patientSummaryUi";
import "../../setting/manageprint/certificatePrint/certificatePrint.css";

type PatientCertificatePrintModalProps = {
  open: boolean;
  onClose: () => void;
  patient: any;
  treatments?: any[];
  theme?: SummaryTheme;
};

type SavedTreatmentOption = {
  id: string;
  label: string;
};

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function buildSavedTreatmentOptions(catalog: any[]): SavedTreatmentOption[] {
  const options: SavedTreatmentOption[] = [];
  const seen = new Set<string>();

  (catalog || [])
    .filter((item) => item?.status1 === true && String(item?.treatment || "").trim())
    .forEach((item) => {
      const main = String(item.treatment).trim();
      const mainId = String(item._id);
      if (!seen.has(mainId)) {
        seen.add(mainId);
        options.push({ id: mainId, label: main });
      }

      (Array.isArray(item.type) ? item.type : [])
        .filter((type: any) => type?.status2 !== false && String(type?.treatment || "").trim())
        .forEach((type: any) => {
          const typeLabel = `${main} · ${String(type.treatment).trim()}`;
          const typeId = `${mainId}:${type._id}`;
          if (!seen.has(typeId)) {
            seen.add(typeId);
            options.push({ id: typeId, label: typeLabel });
          }

          (Array.isArray(type.subType) ? type.subType : [])
            .filter(
              (sub: any) => sub?.status3 !== false && String(sub?.treatment || "").trim()
            )
            .forEach((sub: any) => {
              const subLabel = `${typeLabel} · ${String(sub.treatment).trim()}`;
              const subId = `${typeId}:${sub._id}`;
              if (!seen.has(subId)) {
                seen.add(subId);
                options.push({ id: subId, label: subLabel });
              }
            });
        });
    });

  return options.sort((a, b) => a.label.localeCompare(b.label));
}

function PatientCertificatePrintModal({
  open,
  onClose,
  patient,
  treatments = [],
  theme = "light",
}: PatientCertificatePrintModalProps) {
  const clinic = useLoggedInClinic(open);
  const [store, setStore] = useState<CertificateDesignerStore>(loadCertificateStore);
  const [templateId, setTemplateId] = useState(store.defaultId);
  const [leaveStart, setLeaveStart] = useState(isoToday);
  const [leaveEnd, setLeaveEnd] = useState(isoToday);
  const [reason, setReason] = useState("Dental treatment");
  const [recommendation, setRecommendation] = useState(
    "Rest as advised and attend the next review appointment."
  );
  const [intro, setIntro] = useState("");
  const [catalog, setCatalog] = useState<any[]>([]);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<string[]>([]);
  const [printing, setPrinting] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);
  const isDark = theme === "dark";

  const treatmentOptions = useMemo(() => buildSavedTreatmentOptions(catalog), [catalog]);
  const selectedTreatmentLabels = useMemo(
    () =>
      selectedTreatmentIds
        .map((id) => treatmentOptions.find((item) => item.id === id)?.label)
        .filter(Boolean) as string[],
    [selectedTreatmentIds, treatmentOptions]
  );
  const availableTreatmentOptions = useMemo(
    () => treatmentOptions.filter((item) => !selectedTreatmentIds.includes(item.id)),
    [treatmentOptions, selectedTreatmentIds]
  );

  const template = findTemplate(store.templates, templateId);
  const templateIntro = useMemo(
    () => template.elements.find((item) => item.id === "intro")?.content || "",
    [template]
  );

  useEffect(() => {
    if (!open) return;
    const local = loadCertificateStore();
    setStore(local);
    setTemplateId(local.defaultId);
    setLeaveStart(isoToday());
    setLeaveEnd(isoToday());
    setSelectedTreatmentIds([]);
    setReason("Dental treatment");
    setRecommendation("Rest as advised and attend the next review appointment.");

    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        const saved = response?.data?.data?.certificatePrint;
        if (!saved) return;
        const hydrated = hydrateCertificateStore(saved);
        setStore(hydrated);
        saveCertificateStore(hydrated);
        setTemplateId(hydrated.defaultId);
      })
      .catch(() => {
        /* keep local store */
      });

    baseUrl
      .get("alltreatment/findAllTreatment")
      .then((response) => {
        setCatalog(response?.data?.data || []);
      })
      .catch(() => {
        setCatalog([]);
      });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, patient?._id]);

  useEffect(() => {
    if (!open) return;
    setIntro(templateIntro);
  }, [open, templateId, templateIntro]);

  const page = {
    ...template.page,
    showSafeArea: false,
    logoSrc: resolveCertificateLogo(clinic.printImages, template.page.logoSrc),
  };

  const printElements = useMemo(
    () =>
      template.elements.map((item) =>
        item.id === "intro" ? { ...item, content: intro, visible: true } : item
      ),
    [template.elements, intro]
  );

  const treatmentOverrideText =
    selectedTreatmentLabels.length > 0 ? selectedTreatmentLabels.join(", ") : "";

  const previewData = useMemo(
    () =>
      buildCertificatePrintData({
        patient,
        clinic,
        treatments,
        overrides: {
          leaveStartDate: formatCertificateDate(leaveStart),
          leaveEndDate: formatCertificateDate(leaveEnd),
          leaveDays: inclusiveLeaveDays(leaveStart, leaveEnd),
          reasonForLeave: reason,
          recommendationText: recommendation,
          ...(treatmentOverrideText
            ? {
                treatmentType: treatmentOverrideText,
                treatmentDescription: treatmentOverrideText.toLowerCase(),
              }
            : {}),
        },
      }),
    [
      patient,
      clinic,
      treatments,
      leaveStart,
      leaveEnd,
      reason,
      recommendation,
      treatmentOverrideText,
    ]
  );

  const fieldClass = `h-9 w-full rounded-lg border px-2 text-sm ${
    isDark
      ? "border-slate-600 bg-slate-800 text-slate-100"
      : "border-slate-200 bg-white text-slate-800"
  }`;
  const labelClass = `mb-1 block text-xs font-semibold ${
    isDark ? "text-slate-300" : "text-slate-600"
  }`;

  const handlePrint = async () => {
    setPrinting(true);
    await waitForPaint();
    await printCertificateSheet(paperRef.current);
    setPrinting(false);
  };

  const addTreatment = (id: string) => {
    if (!id || selectedTreatmentIds.includes(id)) return;
    setSelectedTreatmentIds((current) => [...current, id]);
  };

  const removeTreatment = (id: string) => {
    setSelectedTreatmentIds((current) => current.filter((item) => item !== id));
  };

  if (!open) return null;

  return (
    <ModalOverlay
      className="patient-certificate-print-modal fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="patient-certificate-print-title"
    >
      <style>
        {`@media print { @page { size: A4 ${page.orientation}; margin: 0; } }`}
      </style>
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close certificate print"
        data-no-print="true"
      />
      <div
        className={`relative z-10 flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl shadow-xl sm:rounded-2xl ${
          isDark ? "bg-slate-900" : "bg-white"
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-5 ${
            isDark ? "border-slate-700" : "border-slate-200"
          }`}
          data-no-print="true"
        >
          <div>
            <h2
              id="patient-certificate-print-title"
              className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}
            >
              Certificate Print
            </h2>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {previewData.patientName} · uses the saved Manage Print template
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              disabled={printing}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 py-2 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-50"
            >
              <PrintIcon sx={{ fontSize: 16 }} />
              Print
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                isDark
                  ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside
            className={`overflow-auto border-b p-4 lg:border-b-0 lg:border-r ${
              isDark ? "border-slate-700" : "border-slate-200"
            }`}
            data-no-print="true"
          >
            <label className={labelClass}>Template</label>
            <select
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              className={`${fieldClass} mb-3`}
            >
              {store.templates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.id === store.defaultId ? " (Default)" : ""}
                </option>
              ))}
            </select>

            <label className={labelClass}>Certificate intro</label>
            <textarea
              value={intro}
              onChange={(event) => setIntro(event.target.value)}
              rows={5}
              className={`mb-1 w-full rounded-lg border px-2 py-2 text-sm leading-relaxed ${
                isDark
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-200 bg-white text-slate-800"
              }`}
            />
            <p className={`mb-3 text-[11px] leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Edit the certification wording for this print. Placeholders like{" "}
              {"{{patientName}}"}, {"{{treatmentType}}"}, {"{{leaveDays}}"} stay filled
              automatically.
            </p>

            <label className={labelClass}>Treatments</label>
            <select
              value=""
              onChange={(event) => {
                addTreatment(event.target.value);
              }}
              className={`${fieldClass} mb-2`}
              disabled={availableTreatmentOptions.length === 0}
            >
              <option value="">
                {treatmentOptions.length === 0
                  ? "No saved treatments"
                  : availableTreatmentOptions.length === 0
                    ? "All treatments selected"
                    : "Select saved treatment…"}
              </option>
              {availableTreatmentOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            {selectedTreatmentLabels.length > 0 ? (
              <div className="mb-3 flex flex-wrap gap-1.5">
                {selectedTreatmentIds.map((id) => {
                  const label =
                    treatmentOptions.find((item) => item.id === id)?.label || id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => removeTreatment(id)}
                      className={`inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                        isDark
                          ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                      title="Remove treatment"
                    >
                      <span className="truncate">{label}</span>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className={`mb-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Optional. Leave empty to use the latest patient treatment.
              </p>
            )}

            <div className="mb-3 grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Leave from</label>
                <input
                  type="date"
                  value={leaveStart}
                  onChange={(event) => setLeaveStart(event.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Leave to</label>
                <input
                  type="date"
                  value={leaveEnd}
                  onChange={(event) => setLeaveEnd(event.target.value)}
                  className={fieldClass}
                />
              </div>
            </div>
            <p className={`mb-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {inclusiveLeaveDays(leaveStart, leaveEnd)} day(s)
            </p>
            <label className={labelClass}>Reason</label>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={`${fieldClass} mb-3`}
            />
            <label className={labelClass}>Recommendation</label>
            <textarea
              value={recommendation}
              onChange={(event) => setRecommendation(event.target.value)}
              className={`min-h-[72px] w-full rounded-lg border px-2 py-2 text-sm ${
                isDark
                  ? "border-slate-600 bg-slate-800 text-slate-100"
                  : "border-slate-200 bg-white text-slate-800"
              }`}
            />
          </aside>
          <div className={`overflow-auto p-4 ${isDark ? "bg-slate-950" : "bg-slate-100"}`}>
            <div className="flex justify-center">
              <CertificateCanvas
                page={page}
                elements={printElements}
                selectedId={null}
                previewData={previewData}
                interactive={false}
                zoom={0.62}
                paperRef={paperRef}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default PatientCertificatePrintModal;
